import { getBuilderImageResizeUrl } from "@ui/utils/getBuilderImageResizeUrl";
import classNames from "classnames";

import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";

import styles from "./FooterIcons.module.scss";

export type FooterIconsProps = {
  socialTitle?: string;
  socialIcons?: {
    alt: string;
    icon: string;
    link: string;
  }[];
  paymentTitle?: string;
  paymentIcons?: {
    alt: string;
    icon: string;
    href?: string;
    target?: "_blank" | "_self";
    width?: number;
    height?: number;
  }[];
  renderLink?: (props: { href: string; children: React.ReactNode }) => React.ReactNode;
  renderSocialIcon?: (props: { src: string; alt: string }) => JSX.Element;
  showUpdatedFooterUI?: boolean;
  flipIconsOrder?: boolean;
};

const DefaultSocialIcon = ({ src, alt }: { src: string; alt: string }) => <img src={src} alt={alt} />;

/** Shared wrapper for each icon block (payment + social) */
const IconBlock = ({
  title,
  children,
  showUpdatedFooterUI,
}: {
  title?: string;
  showUpdatedFooterUI?: boolean;
  children: React.ReactNode;
}) => {
  if (!children) return null;

  return (
    <div
      className={classNames({
        [styles.iconsContainer]: showUpdatedFooterUI,
        [styles.withTitle]: Boolean(title),
      })}
    >
      {title && <h4 className={styles.title}>{title}</h4>}

      {children}
    </div>
  );
};

export const FooterIcons = ({
  socialTitle,
  socialIcons = [],
  paymentTitle,
  paymentIcons = [],
  renderLink,
  renderSocialIcon = DefaultSocialIcon,
  showUpdatedFooterUI,
  flipIconsOrder,
}: FooterIconsProps) => {
  const renderPaymentIcons = paymentIcons.length > 0 && (
    <IconBlock title={paymentTitle} showUpdatedFooterUI={showUpdatedFooterUI} key="paymentIcons">
      <ul className={styles.paymentIcons}>
        {paymentIcons.map(({ alt, icon, href, target, width, height }, index) => {
          const key = [index, alt, icon, href, target].join("|");
          const optimizedImageSrc = getBuilderImageResizeUrl({ imageUrl: icon, format: "webp" });
          const image = (
            <ImageBehaviour
              src={optimizedImageSrc}
              alt={alt}
              className={styles.paymentIcon}
              width={width}
              height={height}
              nativeBehaviour={true}
            />
          );

          return (
            <li key={key}>
              {href ? (
                <LinkBehaviour target={target} href={href} rel={target === "_blank" ? "noopener noreferrer" : ""}>
                  {image}
                </LinkBehaviour>
              ) : (
                image
              )}
            </li>
          );
        })}
      </ul>
    </IconBlock>
  );

  const renderSocialIcons = socialIcons.length > 0 && (
    <IconBlock title={socialTitle} showUpdatedFooterUI={showUpdatedFooterUI} key="socialIcons">
      <ul className={styles.socialIcons}>
        {socialIcons.map(({ alt, icon, link }, index) => {
          const iconElement = renderSocialIcon({ src: icon, alt });
          const key = [index, alt, icon, link].join("|");

          return (
            <li key={key}>
              {renderLink ? (
                renderLink({ href: link, children: iconElement })
              ) : (
                <a href={link} target="_blank" rel="noopener noreferrer">
                  {iconElement}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </IconBlock>
  );

  const content = [renderPaymentIcons, renderSocialIcons];

  if (showUpdatedFooterUI) return flipIconsOrder ? content.reverse() : content;

  return <div className={classNames(styles.wrapper)}>{content}</div>;
};
